import calculateAll from "../../calculators/calculateAll";
import { charInfoFields, elements } from "../../configs";
import artifacts from "../../data/artifacts";
import {
  findArtifact,
  findCharacter,
  findWeapon
} from "../../data/controllers";
import monsters from "../../data/monsters";
import weapons from "../../data/weapons";
import { findById, findByName } from "../../helpers";
import { initCharInfo, initWeapon } from "./initiators";

export function calculate(state, mode) {
  try {
    const indexes = mode
      ? [...Array(state.setups.length).keys()]
      : [state.curSetupI];
    for (const i of indexes) {
      const __ = calculateAll(
        getThisChar(state.char, i),
        state.charData,
        state.selfMCs[i],
        state.parties[i],
        state.wpRack[i],
        state.subWpMCs[i],
        state.artChest[i],
        state.elmtMCs[i],
        state.customMCs[i],
        state.target
      );
      state.allInfusion[i] = __[0];
      state.allATTRs[i] = __[1];
      state.allRxnBnes[i] = __[3];
      state.allArtSBnes[i] = __[4];
      state.allDmg[i] = __[5];
    }
  } catch (err) {
    console.log(err);
    state.error = true;
  }
}

export function parseData(
  { name, weaponID, artIDs = [null, null, null, null, null], ...info },
  myWps,
  myArts
) {
  const char = { name, ...initCharInfo(info) };

  let wp;
  if (weaponID) {
    const existedWp = findById(myWps, weaponID);
    if (existedWp) {
      const { user, ...weapon } = existedWp;
      wp = {
        ...weapon,
        BCs: getMainWpBCs(existedWp)
      };
    }
  } else {
    const newWp = initWeapon({ type: findCharacter(char).weapon });
    wp = { ...newWp, BCs: getMainWpBCs(newWp) };
  }

  const pieces = artIDs.map((id) => {
    const artP = findById(myArts, id);
    if (artP) {
      const { user, ...info } = artP;
      return info;
    }
    return null;
  });
  const sets = getArtSets(pieces);
  const setCode = sets[0]?.bonusLv === 1 ? sets[0].code : null;
  const art = {
    pieces,
    sets,
    BCs: getMainArtBCs(setCode),
    subBCs: getSubArtBCs(setCode),
    subDCs: getSubArtDCs()
  };
  return [char, wp, art];
}

export function getMainWpBCs(weapon) {
  const result = [];
  const { buffs } = findWeapon(weapon);
  if (buffs) {
    for (const buff of buffs) {
      if (buff.outdated || buff.affect === "teammates") continue;
      const node = { activated: false, index: buff.index };
      if (buff.inputTypes) {
        node.inputs = [];
        buff.inputTypes.forEach((type, j) => {
          if (type === "stacks") {
            node.inputs.push(1);
          } else if (type === "check") {
            node.inputs.push(false);
          } else if (type === "choices") {
            node.inputs.push(buff.values[j][0]);
          } else if (type === "text") {
            node.inputs.push(buff.values[j]);
          }
        });
      }
      result.push(node);
    }
  }
  return result;
}

export function getBCsOfWp(weapon) {
  const BCs = [];
  const { buffs, code } = findWeapon(weapon);
  if (buffs) {
    for (const buff of buffs) {
      if (buff.outdated || buff.affect === "self") continue;
      const node = {
        code,
        activated: false,
        refinement: 1,
        index: buff.index
      };
      if (buff.inputTypes) {
        node.inputs = [];
        buff.inputTypes.forEach((inpType, j) => {
          if (inpType === "choices") {
            node.inputs.push(buff.values[j][0]);
          } else if (inpType === "text") {
            node.inputs.push(0);
          }
        });
      }
      BCs.push(node);
    }
  }
  return BCs;
}

export function getSubWpBCs(type, mainWpCode) {
  const result = [];
  for (const wp of weapons[type]) {
    if (wp.code !== mainWpCode) {
      result.push(...getBCsOfWp({ type, code: wp.code }));
    }
  }
  return result;
}

export function getMainArtBCs(setCode) {
  if (!setCode) return [];
  const result = [];
  const { buffs } = findArtifact({ code: setCode });
  if (buffs) {
    buffs.forEach((buff, index) => {
      if (buff.affect !== "teammates") {
        const node = { activated: false, index };
        if (buff.inputTypes) {
          node.inputs = [];
          buff.inputTypes.forEach((type) => {
            if (type === "stacks") {
              node.inputs.push(buff.startsAt0 ? 0 : 1);
            } else if (type === "crystallize") {
              node.inputs.push("Pyro");
            }
          });
        }
        result.push(node);
      }
    });
  }
  return result;
}

export function getSubArtBCsByCode(code) {
  const result = [];
  const { buffs } = findArtifact({ code });
  if (buffs) {
    buffs.forEach((buff, index) => {
      if (buff.affect !== "self") {
        const node = { code: +code, activated: false, index };
        if (buff.inputTypes) {
          node.inputs = [];
          buff.inputTypes.forEach((type) => {
            if (type === "crystallize") {
              node.inputs.push("Pyro");
            }
          });
        }
        result.push(node);
      }
    });
  }
  return result;
}

export function getSubArtBCs(mainCode) {
  const result = [];
  for (const { code } of artifacts) {
    if (+code !== mainCode) {
      result.push(...getSubArtBCsByCode(code));
    }
  }
  return result;
}

export function getSubArtDCs() {
  return [
    { code: 15, activated: false, index: 0, inputs: ["Pyro"] },
    { code: 33, activated: false, index: 0 }
  ];
}

export function getMonsterInputs(name) {
  const { inputTypes, inputs } = findByName(monsters, name);
  const result = [];
  if (inputTypes) {
    inputTypes.forEach((type, i) => {
      if (type === "variant") {
        result.push(inputs[i][0]);
      } else if (type === "check") {
        result.push(inputs[i]);
      }
    });
  }
  return result;
}

export function getVisionCount(char, party) {
  const count = {};
  count[findCharacter(char).vision] = 1;
  for (const tm of party) {
    if (tm) {
      const { vision } = findCharacter(tm);
      count[vision] = (count[vision] || 0) + 1;
    }
  }
  return count;
}

export function getWpRange(party) {
  const result = {};
  for (const tm of party) {
    if (tm) result[findCharacter(tm).weapon] = 1;
  }
  return result;
}

export function getArtSets(pieces = []) {
  const sets = [];
  const count = {};
  for (const artP of pieces) {
    if (artP) {
      const { code } = artP;
      count[code] = (count[code] || 0) + 1;
      if (count[code] === 2) {
        sets.push({ code, bonusLv: 0 });
      } else if (count[code] === 4) {
        sets[0].bonusLv = 1;
      }
    }
  }
  return sets;
}

export function getThisChar(char, index) {
  if (!char) return null;
  const thisChar = { ...char };
  for (const field of charInfoFields) {
    if (Array.isArray(char[field])) {
      thisChar[field] = char[field][index];
    }
  }
  return thisChar;
}

export function adjustTarget(target, monster) {
  const { res, changeRes } = findByName(monsters, monster.name);
  for (const type in res) {
    if (type === "Elemental RES") {
      for (let elmt of elements) {
        target[elmt + " RES"] = res[type];
      }
    } else {
      target[type] = res[type];
    }
  }
  if (changeRes) changeRes({ target, inputs: monster.inputs });
}

export function getSetupInfo({
  name = "Setup 1",
  ID = Date.now(),
  type = "original"
}) {
  return { name, ID, type };
}

export function getNewSetupName(setups) {
  const existedIs = [1, 2, 3, 4];
  for (const st of setups) {
    const parts = st.name.split(" ");
    if (parts.length === 2 && parts[0] === "Setup" && !isNaN(parts[1])) {
      const i = existedIs.indexOf(+parts[1]);
      if (i !== -1) existedIs.splice(i, 1);
    }
  }
  return "Setup " + existedIs[0];
}
